// import Client from '@/api/client'
import { queryOptions } from '@tanstack/react-query'

import Client from '@/api/client'
import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { getConfig } from '@/app/config'
import { downloadBlob } from '@/utils/download'
import { getItem } from '@/utils/storage'

import { Documents, DocumentType } from '../domain/documents'

const base = getConfig().API_ENDPOINT ?? ''
const client = new Client(base)
const documentsApiUrl = `/api/documents`

const getPathForEntityType = (entityId: string, isNewRequiredActionsModels: boolean) => {
  if (isNewRequiredActionsModels) {
    return entityId.startsWith('B') ? 'booking' : entityId.startsWith('C') ? 'customer' : 'shipment'
  }
  return entityId.startsWith('B') ? 'booking' : 'shipment'
}

const documentsAPIConfig: ApiConfig<{
  downloadDocument: EndpointConfig<{ documentID: string; fileName: string }, Blob, void>
}> = {
  downloadDocument: {
    requestConfig: ({ documentID, fileName }: { documentID: string; fileName: string }) => ({
      endpoint: `${documentsApiUrl}/documents/${documentID}/download`,
      method: 'getFile',
      deserializationStrategy: (result) => downloadBlob(result, fileName),
    }),
  },
}
// Still passing client to clientFactory for now, since not everything in this file has been migrated
export const documentsClient = clientFactory(documentsAPIConfig, client)

type DocumentTypesSearchResult = DocumentType[]

export const getDocumentTypes = async (): Promise<DocumentType[]> => {
  return await client.get<DocumentTypesSearchResult>('/api/documents/document-types/list')
}

export const documentTypesQueryOptions = () =>
  queryOptions({
    queryKey: ['documentTypes'],
    queryFn: getDocumentTypes,
  })

export const getEntityDocuments = async (entityId: string, isNewRequiredActionsModels: boolean): Promise<Documents> => {
  return await client.get<Documents>(
    `/api/documents/documents/${getPathForEntityType(entityId, isNewRequiredActionsModels)}/${entityId}`
  )
}

export const getEntityDocument = async (
  entityId: string,
  documentId: string,
  documentName: string,
  isNewRequiredActionsModels: boolean
): Promise<{ blob: Blob; fileName: string }> => {
  const token = getItem('token')
  const response = await fetch(
    `${base}/api/documents/documents/${getPathForEntityType(entityId, isNewRequiredActionsModels)}/${entityId}/${documentId}/download`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  const blob = await response.blob()
  return { blob, fileName: documentName }
}

export const getEntityAllDocumentsDownload = async (
  customerId: string,
  entityId: string,
  isNewRequiredActionsModels: boolean
): Promise<Blob> => {
  const token = getItem('token')
  // TODO: Replace with `client.getFile` once when non-JSON mime-types are supported
  const response = await fetch(
    `${base}/api/documents/documents/customers/${customerId}/${getPathForEntityType(entityId, isNewRequiredActionsModels)}/${entityId}/download`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  const blob = await response.blob()
  return blob
}

export type DeleteEntityDocumentProps = {
  entityId: string
  documentId: string
  isNewRequiredActionsModels: boolean
}

export const deleteEntityDocument = async (props: DeleteEntityDocumentProps): Promise<void> => {
  await client.delete(
    `/api/documents/documents/${getPathForEntityType(props.entityId, props.isNewRequiredActionsModels)}/${props.entityId}/${props.documentId}`
  )
}

export type CreateEntityDocumentProps = {
  entityId: string
  data: {
    file: File
    state: 'published'
    mimeType: string
    customerID: string
    source: 'customer'
    documentTypeCode: string
  }
  isNewRequiredActionsModels: boolean
}

export const createEntityDocument = async (props: CreateEntityDocumentProps): Promise<Document> => {
  const { file, ...rest } = props.data
  return await client.postFile<Document>(
    `/api/documents/documents/${getPathForEntityType(props.entityId, props.isNewRequiredActionsModels)}/${props.entityId}`,
    file,
    {
      json: JSON.stringify(rest),
    }
  )
}

export type UpdateEntityDocumentProps = {
  entityId: string
  documentId: string
  data: {
    file: File
    state: 'published'
    mimeType: string
    customerID: string
    source: 'customer'
    documentTypeCode: string
  }
  isNewRequiredActionsModels: boolean
}

export const updateEntityDocument = async (props: UpdateEntityDocumentProps): Promise<Document> => {
  const { file, ...rest } = props.data
  return await client.putFile<Document>(
    `/api/documents/documents/${getPathForEntityType(props.entityId, props.isNewRequiredActionsModels)}/${props.entityId}/${props.documentId}`,
    file,
    {
      json: JSON.stringify(rest),
    }
  )
}

export const getShortenedURLToken = async (documentId: string): Promise<{ token: string }> => {
  return await client.post<{ token: string }>('/api/documents/shortened-urls', { documentID: documentId })
}

export const getPreviewURL = (token: string): string => {
  return `${location.origin}/api/documents/shortened-urls/${token}`
}

export const getDocumentPreviewURL = async (documentId: string): Promise<{ previewUrl: string }> => {
  const { token } = await getShortenedURLToken(documentId)
  const previewUrl = getPreviewURL(token)
  return { previewUrl }
}
